import Reveal from "@/components/landing/Reveal";
import { Compass, BadgeCheck, UserCheck, Handshake } from "lucide-react";

const features = [
  {
    id: "01",
    tag: "SMART MATCH",
    title: "Intent-Based Matching",
    desc: "Tell us how you want to work — remote, hybrid, mission-driven, or growth-focused — and meet roles that actually fit.",
    span: "md:col-span-8",
    icon: Compass,
    img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=80",
  },
  {
    id: "02",
    tag: "VERIFIED",
    title: "Hand-Vetted Vacancies",
    desc: "Every listing is verified and salary-transparent. No ghost jobs, no bait postings.",
    span: "md:col-span-4",
    icon: BadgeCheck,
  },
  {
    id: "03",
    tag: "NO NOISE",
    title: "One Profile, Direct Intros",
    desc: "Apply once. We introduce you straight to hiring teams — no black-hole application portals.",
    span: "md:col-span-4",
    icon: UserCheck,
  },
  {
    id: "04",
    tag: "HUMAN ASSIST",
    title: "Career Concierge",
    desc: "Real humans on call throughout your search — from first introduction to signed offer letter.",
    span: "md:col-span-8",
    icon: Handshake,
    img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80",
  },
];

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="py-24 sm:py-32 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <p data-testid="features-eyebrow" className="font-mono text-xs uppercase tracking-[0.25em] text-bronze mb-4">
              The Philosophy · Key Features
            </p>
            <h2 data-testid="features-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight max-w-xl">
              Built around <span className="italic text-bronze">how you want to work</span>
            </h2>
          </div>
          <p className="max-w-xs text-sm sm:text-base font-light text-ink-soft leading-relaxed">
            Four quiet principles shaping every match Vacansee will make.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {features.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.08} className={f.span}>
              <article
                data-testid={`feature-card-${f.id}`}
                className="group relative h-full overflow-hidden rounded-3xl border border-ink/10 bg-white/60 p-7 sm:p-9 transition-all duration-500 hover:border-bronze/40 hover:shadow-[0_20px_60px_rgba(13,27,42,0.10)]"
              >
                {f.img && (
                  <div className="relative mb-7 overflow-hidden rounded-2xl">
                    <img
                      src={f.img}
                      alt={f.title}
                      loading="lazy"
                      className="h-48 sm:h-56 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/25 to-transparent" />
                  </div>
                )}
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
                    {f.id} / {f.tag}
                  </span>
                  <f.icon className="h-5 w-5 text-bronze transition-transform duration-500 group-hover:-rotate-12" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold leading-snug mb-3">{f.title}</h3>
                <p className="text-sm sm:text-base font-light text-ink-soft leading-relaxed">{f.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
